import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { PlaceOutletContextType } from "../home/PlacesHome";
import { Lead, NotificationType } from 'shared/topchef.types';
import placesApi from 'shared/topchef-api-places';
import Loading from 'pages/shared/loading/Loading'
import { NotificationContext } from 'shared/NotificationContext';
import { useErrorBoundary } from 'react-error-boundary';
import PublicLeadContent from 'pages/shared/public-lead-content/PublicLeadContent';

const LeadToCloseDetails = () => {

  const navigate = useNavigate();
  const [loggedInPlace] = useOutletContext<PlaceOutletContextType>();

  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;

  const params = useParams();
  const leadId: string = params.leadId || '';

  const [isReady, setReady] = useState<boolean>(false);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);

  const { showBoundary } = useErrorBoundary();

  interface billLeadForm {
    billContent: File | null;
    amount: number;
  }
    
  const [billFormData, setBillFormData] = useState<billLeadForm>({
    billContent: null,
    amount: 0,
  });

  useEffect(() => {

    async function loadLead() {
      try {
        const lead: Lead = await placesApi.loadLeadToClose(loggedInPlace!.id, leadId);
        setCurrentLead(lead);
        setReady(true);
      } catch (error: any) { 
        const newError = new Error(`Problème pour récupérer le brief à clôturer : '${error.message}'`);
        triggerNotification({type: NotificationType.error, message : `${newError.message}`}); 
        showBoundary(newError);
      } 
    }

    if (isReady) {
      return;
    }

    loadLead();

  }, [currentLead, isReady, leadId, loggedInPlace, showBoundary, triggerNotification]);


  const shareBill = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await placesApi.shareBill(loggedInPlace!.id, leadId, billFormData.billContent!, billFormData.amount!);
      triggerNotification({type: NotificationType.information, message : "Facture partagée avec succès"}); 
      navigate('/places/leads/to-close');
    } catch (error: any) {
      const newError = new Error(`Problème pour partager la facture : '${error.message}'`);
      triggerNotification({type: NotificationType.error, message : `${newError.message}`}); 
      showBoundary(newError);
    }

  };

  const handleInputEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const name = event.target.name;

    let value : string | File;
    if (event.target.files == null) {
      value = event.target.value
    }
    else {
      value = event.target.files[0];
    }

    setBillFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };


  if (!isReady) {
    return (
      <Loading />
    );
  }

  if (currentLead!.status === 'signed') {
    return (
      <div className="container page-briefs-single-details p-5 col-md-6 overflow-auto">
        
        <div className="col-md-6 brief-details-col">
          <PublicLeadContent publicLead={currentLead!} />
        </div>

        <div className="col-md-6 invoice-upload-col p-5 overflow-auto row d-flex align-items-center justify-content-center">
          <h3 className="mb-3 text-center">Transmettre votre facture</h3>
          <div id="event_details_form" className="col-md-9 text-center card mt-2">
            <form id="invoice_details_form" className="d-flex row justify-content-center p-3 pb-4" onSubmit={shareBill}>
              <div className="mb-4 col-md-12 text-center justify-content-center">
                <label htmlFor="invoice_price" className="form-label">Montant de la facture</label>
                  <input id="invoice_price" name='amount' min='1' onChange={handleInputEventChange} type="number" className="col-md-6 text-center" placeholder="Montant HT en €" required/>
              </div>
              <div className="mb-4 row">
                <div className="col-md-12">
                  <label className="col-md-12" htmlFor="bill-file-input">
                    Glissez/déposez votre facture ici
                    <strong className="m-2">OU</strong>
                    <input id="bill-file-input" name="billContent" type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleInputEventChange} data-testid="bill-file-input" required/>
                  </label>
                </div>     
              </div>                  
              <div className="text-center">
                <button type="submit" id="save_brief_details" className="btn btn-primary m-3" data-testid="share-bill-button">Envoyer</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }


  function getLeadStatusDisplayMessage() {
    if (currentLead!.status === 'billed') {
      return "Facture en cours de validation... 🚀";
    }
    if (currentLead!.status === 'verified') {
      return "Facture validée ! 🎉";
    }
    return "";
  }

  return (
    <div className="container p-2 page-briefs-single-details col-md-6 overflow-auto">
      <div id="lead_sidebar" className="d-flex">
        <ul className="nav nav-pills flex-column mb-auto">
          <li className="nav-item"> <a href="/places/share-a-lead" type="button" className="btn btn-outline-primary nav-link add-brief-link">Ajouter un brief</a></li>
        </ul>
      </div>
       
      <PublicLeadContent publicLead={currentLead!} />

      <div className="text-center">
        <div className="alert alert-success brief-status-details" role="alert">
          {getLeadStatusDisplayMessage()}
        </div>     
      </div>
    </div>
  );

}


export default LeadToCloseDetails;
